import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/AuthProvider";
import { useStableAvatar } from "@/hooks/use-stable-avatar";

export interface ReadingStats {
  // Core data
  readingSessions: any[];
  uniqueBooksCompleted: number;
  currentStreak: number;
  readingGoal: number;
  totalSessions: number;
  lastSession: any | null;
  
  // Time-based stats
  totalReadingTime: number;        // Total minutes read
  averageSessionTime: number;      // Avg minutes per session
  longestSession: number;          // Longest session in minutes
  
  // Session stats
  sessionsToday: number;           // Sessions completed today
  booksThisMonth: number;          // Unique books this month
  
  // Consistency
  readingConsistency7d: number;    // % of days read in last 7 days
  readingConsistency30d: number;   // % of days read in last 30 days
  
  // Time-of-day
  nightReadsThisWeek: number;      // Sessions after 7pm in last 7 days
  
  // Re-reads
  rereadBooks: number;             // Books read more than once
  
  // Scenes (cinematic metric)
  scenesExperienced: number;       // Total page images viewed
  
  // Pacing label
  readingPace: 'Chill pace' | 'Steady reader' | 'Quick read';
  
  // NEW: Favorite book stats
  favoriteBook: { id: number; title: string; sessions: number } | null;
  mostRepeats: number;             // Max sessions on a single book
}

/**
 * Unified hook for all profile-specific reading data
 * Combines reading progress, stats, streaks in optimized queries
 */
export function useProfileReadingData() {
  const { session } = useAuth();
  const { currentProfile } = useStableAvatar();
  const userId = session?.user?.id;

  const defaultData: ReadingStats = {
    readingSessions: [],
    uniqueBooksCompleted: 0,
    currentStreak: 0,
    readingGoal: 12,
    totalReadingTime: 0,
    averageSessionTime: 0,
    longestSession: 0,
    sessionsToday: 0,
    booksThisMonth: 0,
    readingConsistency7d: 0,
    readingConsistency30d: 0,
    nightReadsThisWeek: 0,
    rereadBooks: 0,
    scenesExperienced: 0,
    readingPace: 'Chill pace',
    lastSession: null,
    totalSessions: 0,
    favoriteBook: null,
    mostRepeats: 0
  };

  // Combined query for all reading-related data for the current profile
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['profileReadingData', userId, currentProfile?.id],
    queryFn: async (): Promise<ReadingStats> => {
      if (!userId || !currentProfile?.id) {
        return defaultData;
      }

      // Fetch reading sessions with needed columns (bounded query)
      const { data: sessions, error: sessionsError } = await supabase
        .from('reading_sessions')
        .select('book_id, start_time, end_time, current_page, duration_minutes')
        .eq('user_id', userId)
        .eq('profile_id', currentProfile.id)
        .order('start_time', { ascending: false })
        .limit(500);

      if (sessionsError) {
        console.error('Error fetching reading sessions:', sessionsError);
        throw sessionsError;
      }

      // Fetch profile settings for reading goal
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('profile_settings')
        .eq('id', currentProfile.id)
        .single();

      if (profileError) {
        console.error('Error fetching profile settings:', profileError);
      }

      // Prioritize readingGoal over legacy reading_goal field
      const readingGoal = (profileData?.profile_settings as any)?.readingGoal ?? (profileData?.profile_settings as any)?.reading_goal ?? 12;
      const readingSessions = sessions || [];

      // Calculate unique books completed
      const completedSessions = readingSessions.filter(session => 
        session.end_time && session.current_page > 0
      );
      const uniqueBookIds = new Set(completedSessions.map(session => session.book_id));
      const uniqueBooksCompleted = uniqueBookIds.size;

      // Calculate reading streak from sessions
      const currentStreak = calculateStreakFromSessions(readingSessions);

      // ===== TIME-BASED STATS =====
      const totalReadingTime = readingSessions.reduce((total, session) => {
        return total + (session.duration_minutes || 0);
      }, 0);

      const averageSessionTime = readingSessions.length > 0 
        ? Math.round(totalReadingTime / readingSessions.length) 
        : 0;

      const longestSession = readingSessions.reduce((max, session) => {
        return Math.max(max, session.duration_minutes || 0);
      }, 0);

      // ===== SESSION STATS =====
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const sessionsToday = readingSessions.filter(session => {
        const sessionDate = new Date(session.start_time);
        sessionDate.setHours(0, 0, 0, 0);
        return sessionDate.getTime() === today.getTime();
      }).length;

      // Unique books read this month
      const thisMonth = new Date();
      const booksThisMonthSet = new Set(
        readingSessions.filter(session => {
          const sessionDate = new Date(session.start_time);
          return sessionDate.getMonth() === thisMonth.getMonth() && 
                 sessionDate.getFullYear() === thisMonth.getFullYear();
        }).map(s => s.book_id)
      );
      const booksThisMonth = booksThisMonthSet.size;

      // ===== CONSISTENCY STATS =====
      const readingConsistency7d = calculateConsistency(readingSessions, 7);
      const readingConsistency30d = calculateConsistency(readingSessions, 30);

      // ===== NIGHT READS (after 7pm) =====
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      
      const nightReadsThisWeek = readingSessions.filter(session => {
        const sessionDate = new Date(session.start_time);
        const hour = sessionDate.getHours();
        return sessionDate >= sevenDaysAgo && hour >= 19; // 7pm or later
      }).length;

      // ===== RE-READS & FAVORITE BOOK =====
      const bookSessionCounts: Record<number, number> = {};
      readingSessions.forEach(session => {
        bookSessionCounts[session.book_id] = (bookSessionCounts[session.book_id] || 0) + 1;
      });
      const rereadBooks = Object.values(bookSessionCounts).filter(count => count > 1).length;
      
      // Find most repeated book (favorite)
      let favoriteBookId: number | null = null;
      let mostRepeats = 0;
      Object.entries(bookSessionCounts).forEach(([bookId, count]) => {
        if (count > mostRepeats) {
          favoriteBookId = parseInt(bookId);
          mostRepeats = count;
        }
      });

      // ===== SCENES EXPERIENCED (pages viewed) =====
      const scenesExperienced = readingSessions.reduce((total, session) => {
        return total + (session.current_page || 0);
      }, 0);

      // ===== READING PACE =====
      const avgPagesPerMinute = totalReadingTime > 0 
        ? scenesExperienced / totalReadingTime 
        : 0;
      
      let readingPace: 'Chill pace' | 'Steady reader' | 'Quick read';
      if (avgPagesPerMinute < 0.3) {
        readingPace = 'Chill pace';
      } else if (avgPagesPerMinute < 0.6) {
        readingPace = 'Steady reader';
      } else {
        readingPace = 'Quick read';
      }

      const lastSession = readingSessions[0] || null;

      // Fetch favorite book title if we have one
      let favoriteBook: { id: number; title: string; sessions: number } | null = null;
      if (favoriteBookId && mostRepeats > 0) {
        const { data: bookData } = await supabase
          .from('books')
          .select('id, title')
          .eq('id', favoriteBookId)
          .single();
        
        if (bookData) {
          favoriteBook = {
            id: bookData.id,
            title: bookData.title,
            sessions: mostRepeats
          };
        }
      }

      return {
        readingSessions,
        uniqueBooksCompleted,
        currentStreak,
        readingGoal,
        totalReadingTime,
        averageSessionTime,
        longestSession,
        sessionsToday,
        booksThisMonth,
        readingConsistency7d,
        readingConsistency30d,
        nightReadsThisWeek,
        rereadBooks,
        scenesExperienced,
        readingPace,
        lastSession,
        totalSessions: readingSessions.length,
        favoriteBook,
        mostRepeats
      };
    },
    enabled: !!userId && !!currentProfile?.id,
    staleTime: 30 * 1000, // 30 seconds for faster sync
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: 60 * 1000, // Background refresh every 60 seconds
  });

  return {
    data: data || defaultData,
    isLoading: isLoading || !currentProfile?.id,
    error,
    refetch
  };
}

/**
 * Calculate reading consistency (% of days with sessions in period)
 */
function calculateConsistency(sessions: any[], days: number): number {
  if (!sessions || sessions.length === 0) return 0;

  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - days);
  cutoffDate.setHours(0, 0, 0, 0);

  const uniqueDatesInPeriod = new Set(
    sessions
      .filter(session => new Date(session.start_time) >= cutoffDate)
      .map(session => {
        const date = new Date(session.start_time);
        date.setHours(0, 0, 0, 0);
        return date.getTime();
      })
  );

  return Math.round((uniqueDatesInPeriod.size / days) * 100);
}

/**
 * Calculate reading streak from reading sessions
 */
function calculateStreakFromSessions(sessions: any[]): number {
  if (!sessions || sessions.length === 0) return 0;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let streak = 0;
  let currentDate = new Date(today);

  // Get unique dates from sessions
  const sessionDates = sessions.map(session => {
    const date = new Date(session.start_time);
    date.setHours(0, 0, 0, 0);
    return date.getTime();
  });

  const uniqueDates = [...new Set(sessionDates)].sort((a, b) => b - a);

  for (const sessionTime of uniqueDates) {
    if (sessionTime === currentDate.getTime()) {
      streak++;
      currentDate.setDate(currentDate.getDate() - 1);
    } else if (sessionTime === currentDate.getTime() + 24 * 60 * 60 * 1000) {
      // If today has no reading but yesterday does, still count the streak
      if (streak === 0) {
        streak++;
        currentDate.setDate(currentDate.getDate() - 2);
      } else {
        break;
      }
    } else {
      break;
    }
  }

  return streak;
}
